import { autoUpdate, FloatingPortal, offset, size, useFloating } from "@floating-ui/react";
import type { CSSProperties, ReactNode, RefCallback } from "react";

interface RuntimeFloatingPanelOptions {
  open: boolean;
  mainAxis?: number;
  crossAxis?: number;
  viewportPadding?: number;
  placement?: "bottom" | "bottom-start" | "right" | "right-start" | "right-end" | "top";
}

export interface RuntimeFloatingPanelAttributes {
  ref: RefCallback<HTMLElement>;
  style: CSSProperties;
}

interface RuntimeFloatingPanelResult {
  referenceRef: RefCallback<HTMLElement>;
  panelProps: RuntimeFloatingPanelAttributes;
}

/** Keeps runtime floating geometry and the Floating UI dependency out of product modules. */
export function useRuntimeFloatingPanel({
  open,
  mainAxis = 0,
  crossAxis = 0,
  viewportPadding = 0,
  placement = "bottom-start",
}: RuntimeFloatingPanelOptions): RuntimeFloatingPanelResult {
  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    placement,
    open,
    middleware: [
      offset({ mainAxis, crossAxis }),
      size({
        padding: viewportPadding,
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, { maxHeight: `${availableHeight}px` });
        },
      }),
    ],
  });

  return {
    referenceRef: refs.setReference,
    panelProps: {
      ref: refs.setFloating,
      style: floatingStyles,
    },
  };
}

export function RuntimeFloatingPortal({ children }: { children: ReactNode }): React.ReactNode {
  return <FloatingPortal>{children}</FloatingPortal>;
}
